/**
 * May this operator perform payment operations on this academy — asked ONCE, off an order.
 *
 * WHY THIS EXISTS. `controls` is the only thing that answers "may I write here", and until now
 * it only ever arrived attached to something: an order screen, or the audit list. The free
 * enrolment operation has no order — that is its defining property — so a surface offering it
 * had nowhere to read the gate from, and the previous state of this branch was a dialog with no
 * call site at all.
 *
 * WHICH ENDPOINT, AND WHY NOT THE AUDIT ONE. `GET orders` is paginated: `limit: 1` is three SQL
 * queries and one row, and the payload carries `controls` in full. `GET audit` also carries
 * `controls`, but `audit.timeline()` walks every action row this academy has ever recorded and
 * collapses them in Python — an unbounded read to answer a boolean.
 *
 * THREE ANSWERS, NOT TWO, AND `dark` IS THE THIRD. A deployment with
 * `THINLMS_PAYMENT_OPS_ENABLED` off answers `404 not_enabled` to every route here, and that is
 * not a refusal aimed at this operator — it is the whole feature being absent. A surface must
 * render NOTHING for it, not a sentence about authority that would send somebody to ask their
 * academy for a permission that does not exist anywhere yet. A refusal that names a gate
 * (`write_not_enabled`, `mandate_required`, `no_write_authority`) is the opposite: it has a next
 * step, and `writeGate` already turns each one into its own sentence.
 *
 * AN UNANSWERED QUERY IS NOT PERMISSION. While the read is in flight, or if it fails for a
 * reason that is not `not_enabled`, `gate` is `writeGate(undefined)` — `kind: 'unknown'` — which
 * every caller must treat as read-only. That is `writeGate`'s own documented rule and this hook
 * does not soften it.
 */

import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../shared/lib/openedx';
import {
  explainOpsFailure,
  loadOrders,
  orderListKey,
  writeGate,
  type WireControls,
  type WriteGate,
} from '../api/paymentOps';

/** One row, because the row is not the point — `controls` is. */
const GATE_FILTERS = { limit: 1 } as const;

export interface OpsGate {
  /** The gate. `unknown` until the read lands, and `unknown` is never permission. */
  gate: WriteGate;
  /**
   * Payment operations are not installed on this deployment at all.
   *
   * Render nothing — no button, no explanation. There is no authority anybody could grant.
   */
  dark: boolean;
  /** The read has not answered yet. Distinct from `dark` and from a refusal. */
  loading: boolean;
}

/** True when a failure means "this feature does not exist here", not "not you". */
export function opsIsDark(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return explainOpsFailure(error).code === 'not_enabled';
}

export function useOpsGate(): OpsGate {
  const query = useQuery<{ controls: WireControls }>({
    queryKey: orderListKey(GATE_FILTERS),
    queryFn: () => loadOrders(GATE_FILTERS),
    // A refusal and a dark deployment are both stable answers about the deployment and the
    // operator, not transient faults. Retrying asks the same question twice more and delays
    // the screen.
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    gate: writeGate(query.data?.controls),
    dark: query.isError && opsIsDark(query.error),
    loading: query.isPending,
  };
}
